window.theme = window.theme || {};

theme.utils = Object.assign(theme.utils || {}, {
  debounce: (fn, wait = 300) => {
    let timeoutId = null;

    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fn(...args), wait);
    };
  },
  fetchConfig: (type = 'json') => ({
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  }),
  domParser: new DOMParser(),

  togglePanel(trigger, panel) {
    const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
    if (isExpanded) {
      this.closePanel(trigger, panel);
    } else {
      this.openPanel(trigger, panel);
    }
    return !isExpanded;
  },

  openPanel(trigger, panel) {
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    if (panel) panel.setAttribute('aria-hidden', 'false');
  },

  closePanel(trigger, panel) {
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (panel) panel.setAttribute('aria-hidden', 'true');
  }
});

theme.utils.createDragToClose = function ({
  host,
  panel,
  scrollRegionSelector = null,
  interactiveSelector = 'a, button, input, select, textarea, [role="button"]',
  commitThreshold = 6,
  closeThreshold = 80,
  onClose,
}) {
  let pointerId = null;
  let startY = 0;
  let committed = false;
  let scrollRegion = null;

  const reset = () => {
    pointerId = null;
    committed = false;
    scrollRegion = null;
    host.classList.remove('is-dragging');
    panel.style.removeProperty('touch-action');
    panel.style.removeProperty('--drawer-drag-offset');
  };

  const onDragStart = (event) => {
    if (pointerId !== null) return;
    const control = event.target.closest(interactiveSelector);
    if (control && panel.contains(control)) return;

    const region = scrollRegionSelector ? event.target.closest(scrollRegionSelector) : panel;
    if (region && region.scrollTop > 0) return;

    pointerId = event.pointerId;
    startY = event.clientY;
    committed = false;
    scrollRegion = region;
  };

  const onDragMove = (event) => {
    if (event.pointerId !== pointerId) return;

    const delta = event.clientY - startY;

    if (!committed) {
      if (delta < commitThreshold || scrollRegion?.scrollTop > 0) return;
      committed = true;
      host.classList.add('is-dragging');
      panel.style.setProperty('touch-action', 'none');
    }

    event.preventDefault();
    panel.style.setProperty('--drawer-drag-offset', `${Math.max(0, delta)}px`);
  };

  const onDragEnd = (event) => {
    if (event.pointerId !== pointerId) return;

    const wasCommitted = committed;
    const delta = wasCommitted ? Math.max(0, (event.clientY ?? startY) - startY) : 0;
    reset();
    if (!wasCommitted) return;

    if (delta > closeThreshold) onClose();
  };

  return { onDragStart, onDragMove, onDragEnd, reset };
};

theme.utils.restockNotify = (() => {
  const readyCallbacks = [];
  let ready = false;

  const isAppLoaded = () => typeof window._RestockRocket !== 'undefined';

  const resolveReady = () => {
    if (ready) return;
    ready = true;
    readyCallbacks.splice(0).forEach((callback) => callback());
  };

  if (isAppLoaded()) {
    resolveReady();
  } else {
    document.addEventListener('stoq:loaded', resolveReady, { once: true });

    const maxAttempts = 20;
    const pollInterval = 100;
    let attempts = 0;

    const poll = () => {
      attempts++;
      if (isAppLoaded()) {
        document.removeEventListener('stoq:loaded', resolveReady);
        resolveReady();
        return;
      }
      if (attempts < maxAttempts) setTimeout(poll, pollInterval);
    };

    setTimeout(poll, pollInterval);
  }

  return {
    onReady(callback) {
      if (ready) callback();
      else readyCallbacks.push(callback);
    },
    openModal(productJson, variantId) {
      const numericVariantId = Number(variantId);
      if (!ready || !productJson || !numericVariantId) return false;
      window._RestockRocket.openModal(productJson, numericVariantId);
      return true;
    },
  };
})();

(() => {
  const SELECTOR = 'dialog[scroll-lock][open], details[scroll-lock][open], theme-drawer[data-open], theme-modal[data-open]';
  const hasSupport = typeof document.documentElement.matches === 'function';

  let locked = false;
  let savedY = 0;

  const isActive = () => {
    if (hasSupport && CSS.supports('selector(:has(*))')) {
      return document.documentElement.matches(`:has(${SELECTOR})`);
    }
    return !!document.querySelector(SELECTOR);
  };

  const setOverlayViewportWidth = () => {
    document.documentElement.style.setProperty('--overlay-viewport-width', `${window.innerWidth}px`);
  };

  const clearOverlayViewportWidth = () => {
    document.documentElement.style.removeProperty('--overlay-viewport-width');
  };

  const lock = () => {
    savedY = window.scrollY;
    Object.assign(document.body.style, {
      position: 'fixed',
      top: `-${savedY}px`,
      left: '0',
      right: '0',
      width: '100%',
    });
    locked = true;
  };

  const unlock = () => {
    Object.assign(document.body.style, {
      position: '',
      top: '',
      left: '',
      right: '',
      width: '',
    });
    window.scrollTo(0, savedY);
    locked = false;
  };

  const evaluate = () => {
    const mobile = window.matchMedia('(max-width: 1023px)').matches;
    const active = isActive();

    active ? setOverlayViewportWidth() : clearOverlayViewportWidth();

    if (active && !locked && mobile) {
      lock();
    } else if (!active && locked) {
      unlock();
    }
  };

  new MutationObserver(evaluate).observe(document.documentElement, {
    subtree: true,
    attributes: true,
    attributeFilter: ['open', 'data-open'],
  });

  window.addEventListener(
    'resize',
    theme.utils.debounce(() => {
      if (isActive()) setOverlayViewportWidth();
    }, 150)
  );
})();

(() => {
  const SCROLLBAR_CLASS = 'custom-scrollbar';
  const SCROLLING_CLASS = 'is-scrolling';
  const HIDE_DELAY = 700;
  const timers = new WeakMap();

  document.addEventListener('scroll', (event) => {
    const target = event.target;
    if (!target?.classList?.contains(SCROLLBAR_CLASS)) return;

    target.classList.add(SCROLLING_CLASS);
    clearTimeout(timers.get(target));
    timers.set(target, setTimeout(() => target.classList.remove(SCROLLING_CLASS), HIDE_DELAY));
  }, { capture: true, passive: true });
})();

(() => {
  const WRAPPER_SELECTOR = '.image--skeleton';

  const markLoaded = (img) => {
    img.closest(WRAPPER_SELECTOR)?.classList.add('is-loaded');
  };

  document.querySelectorAll(`${WRAPPER_SELECTOR} img`).forEach((img) => {
    if (img.complete) markLoaded(img);
  });

  const handleImageEvent = (event) => {
    if (event.target.tagName === 'IMG') markLoaded(event.target);
  };

  document.addEventListener('load', handleImageEvent, { capture: true, passive: true });
  document.addEventListener('error', handleImageEvent, { capture: true, passive: true });
})();

theme.cache = (() => {
  const PREFIX = 'loomify:';
  const TTL_SHORT = 5 * 60 * 1000;
  const MAX_CACHEABLE_LENGTH = 250000;
  const TTL_FRESH = 60 * 1000;
  const EPOCH_KEY = 'volatile-epoch';
  const memory = new Map();
  const inflight = new Map();

  const isExpired = (expiresAt) => typeof expiresAt === 'number' && Date.now() > expiresAt;

  const scope = () => {
    const shopify = window.Shopify || {};
    return [shopify.locale, shopify.country, shopify.currency?.active].join('|');
  };

  const cache = {
    TTL_SHORT,
    TTL_FRESH,
    MAX_CACHEABLE_LENGTH,

    scopedKey(key) {
      return `${key}|${scope()}`;
    },

    invalidateVolatile() {
      cache.delete(EPOCH_KEY);
      cache.set(EPOCH_KEY, Date.now(), 0);
      inflight.clear();
    },

    request(url, { ttl = TTL_FRESH, read = (response) => response.text(), init } = {}) {
      const key = cache.scopedKey(String(url));
      const epoch = cache.get(EPOCH_KEY) || 0;
      const hit = cache.get(key);

      if (hit && hit.at > epoch) return Promise.resolve(hit.value);
      if (inflight.has(key)) return inflight.get(key);

      const startedAt = Date.now();
      const promise = fetch(url, init)
        .then((response) => {
          if (!response.ok) throw new Error(`Request failed: ${response.status}`);
          return read(response);
        })
        .then((value) => {
          const tooLarge = typeof value === 'string' && value.length > MAX_CACHEABLE_LENGTH;
          if (!tooLarge && startedAt > (cache.get(EPOCH_KEY) || 0)) cache.set(key, { at: startedAt, value }, ttl);
          return value;
        })
        .finally(() => {
          if (inflight.get(key) === promise) inflight.delete(key);
        });

      inflight.set(key, promise);
      return promise;
    },

    get(key) {
      const storageKey = `${PREFIX}${key}`;

      try {
        const raw = sessionStorage.getItem(storageKey);
        if (raw !== null) {
          const entry = JSON.parse(raw);
          if (isExpired(entry.expiresAt)) {
            sessionStorage.removeItem(storageKey);
            return null;
          }
          return entry.value;
        }
      } catch (error) {}

      const entry = memory.get(storageKey);
      if (!entry) return null;
      if (isExpired(entry.expiresAt)) {
        memory.delete(storageKey);
        return null;
      }
      return entry.value;
    },

    set(key, value, ttl = TTL_SHORT) {
      const storageKey = `${PREFIX}${key}`;
      const expiresAt = ttl ? Date.now() + ttl : null;

      try {
        sessionStorage.setItem(storageKey, JSON.stringify({ value, expiresAt }));
        return;
      } catch (error) {}

      memory.set(storageKey, { value, expiresAt });
    },

    delete(key) {
      const storageKey = `${PREFIX}${key}`;
      try {
        sessionStorage.removeItem(storageKey);
      } catch (error) {}
      memory.delete(storageKey);
    },

    clear() {
      try {
        Object.keys(sessionStorage)
          .filter((storageKey) => storageKey.startsWith(PREFIX))
          .forEach((storageKey) => sessionStorage.removeItem(storageKey));
      } catch (error) {}
      memory.clear();
    },
  };

  return cache;
})();

theme.pubsub = (() => {
  const subscribers = new Map();

  return {
    events: Object.freeze({
      CART_REQUEST_START: 'cart:request-start',
      CART_REQUEST_END: 'cart:request-end',
      CART_UPDATE: 'cart-update',
      CART_ADD: 'cart:add',
      CART_REMOVE: 'cart:remove',
      CART_CLEAR: 'cart:clear',
      CART_DRAWER_OPEN: 'cart-drawer:open',
      CART_DRAWER_CLOSE: 'cart-drawer:close',
      QUANTITY_UPDATE: 'quantity-update',
      VARIANT_CHANGE: 'variant-change',
      OPTION_VALUE_CHANGE: 'option-value-selection-change',
      CART_ERROR: 'cart-error',
      CART_ITEM_ERROR: 'cart:item-error',
      CART_FOOTER_ERROR: 'cart:footer-error',
      SEARCH_QUERY: 'search:query',
      SEARCH_RESULTS: 'search:results',
      FILTER_APPLIED: 'filter:applied',
      FILTER_CLEARED: 'filter:cleared',
      SORT_CHANGED: 'sort:changed',
      DRAWER_OPEN: 'drawer:open',
      DRAWER_CLOSE: 'drawer:close',
      MODAL_OPEN: 'modal:open',
      MODAL_CLOSE: 'modal:close',
      PAGE_TRANSITION: 'page:transition',
      PAGE_RESTORED: 'page:restored',
      FORM_SUBMIT: 'form:submit',
      FORM_ERROR: 'form:error',
      FORM_SUCCESS: 'form:success',
      SLIDER_CHANGE: 'slider:change',
      SLIDER_AUTOROTATE_TOGGLE: 'slider:autorotate-toggle',
      TOAST_SHOW: 'toast:show',
      TOAST_DISMISS: 'toast:dismiss',
      CONSENT_UPDATED: 'consent:updated',
    }),

    subscribe(eventName, callback) {
      if (!subscribers.has(eventName)) {
        subscribers.set(eventName, new Set());
      }
      subscribers.get(eventName).add(callback);

      return () => subscribers.get(eventName)?.delete(callback);
    },

    publish(eventName, data) {
      subscribers.get(eventName)?.forEach((cb) => cb(data));
    },

    clear(eventName) {
      eventName ? subscribers.delete(eventName) : subscribers.clear();
    },
  };
})();

[theme.pubsub.events.CART_UPDATE, theme.pubsub.events.CART_ADD].forEach((eventName) => {
  theme.pubsub.subscribe(eventName, () => theme.cache.invalidateVolatile());
});

window.addEventListener('pageshow', (event) => {
  if (event.persisted) theme.pubsub.publish(theme.pubsub.events.PAGE_RESTORED);
});

